import ApiSmile from '@/api/api-smile';
import ActionArea from '@/component/action-area';
import {ActionButton} from '@/component/action-area/action-area';
import DataRow from '@/component/data-row';
import PageContainer from '@/component/page-container';
import LoadingUtil from '@/util/loading-util';
import MessageUtil from '@/util/message-util';
import {DatePicker, Form, InputNumber, Modal, Select} from 'antd';
import TextArea from 'antd/lib/input/TextArea';
import moment, {Moment} from 'moment';
import React, {useEffect, useState} from 'react';
import {useNavigate} from 'react-router';
import './hold-buy.scss';

interface HoldBuyData {
    code: string;
    name: string;
    quantity: number;
    buyTime: Moment;
    buyPrice: number;
    buyDescription: string;
}

interface InfoBrief {
    id: number;
    code: string;
    name: string;
}

const HoldBuy = () => {
    const navigate = useNavigate();
    const [form] = Form.useForm();
    const [infoBrief, setInfoBrief] = useState<InfoBrief[]>([]);

    useEffect(() => {
        form.setFieldsValue({
            buyTime: moment(),
            buyPrice: 0,
            quantity: 0,
            sellDescription: '',
        });
    }, [form]);

    useEffect(() => {
        const load = async () => {
            LoadingUtil.show();
            const response: any = await ApiSmile.getInfoBrief();
            if (response?.data?.code === 0) {
                setInfoBrief(response.data.data || []);
            } else {
                const msg = response?.data?.msg || '网络请求失败';
                MessageUtil.error(msg);
            }
            LoadingUtil.hide();
        };

        load();
    }, []);

    const onCancelClick = () => {
        navigate('/hold', {replace: true});
    };

    const validateData = (payload: HoldBuyData) => {
        if (!(payload.code && payload.name)) {
            MessageUtil.error('请选择名称和代码');
            return false;
        }
        if (!payload.buyTime) {
            MessageUtil.error('买入时间不能为空');
            return false;
        }
        if (!Number.isFinite(payload.buyPrice)) {
            MessageUtil.error('买入价格格式错误');
            return false;
        }
        if (payload.buyPrice <= 0) {
            MessageUtil.error('买入价格不能小于0');
            return false;
        }
        if (!Number.isFinite(payload.quantity)) {
            MessageUtil.error('买入数量格式错误');
            return false;
        }
        if (payload.quantity <= 0) {
            MessageUtil.error('买入数量不能小于0');
            return false;
        }

        return true;
    };

    const onSaveClick = () => {
        const value = form.getFieldsValue() || {};
        const payload: HoldBuyData = {
            code: value.code || '',
            name: infoBrief.find((i) => i.code === value.code)?.name || '',
            quantity: Number(value.quantity) || 0,
            buyTime: value.buyTime,
            buyPrice: Number(value.buyPrice) || 0,
            buyDescription: value.buyDescription || '',
        };
        if (!validateData(payload)) {
            return;
        }

        const create = async () => {
            LoadingUtil.show();
            const response: any = await ApiSmile.createHoldBuy(payload);
            if (response?.data?.code === 0) {
                navigate(`/hold/${response.data.data.id}`, {replace: true});
                MessageUtil.info('保存成功');
            } else {
                const msg = response?.data?.msg || '网络请求失败';
                MessageUtil.error(msg);
            }
            LoadingUtil.hide();
        };

        Modal.confirm({
            title: '确定买入吗？',
            content: (
                <div>
                    <div>{`代码：${payload.code}`}</div>
                    <div>{`名称：${payload.name}`}</div>
                    <div>{`数量：${payload.quantity}`}</div>
                    <div>{`时间：${payload.buyTime.format('YYYY-MM-DD')}`}</div>
                    <div>{`价格：${payload.buyPrice}`}</div>
                    <div>{`描述：${payload.buyDescription}`}</div>
                </div>
            ),
            onOk: create,
        });
    };

    const getButtons = () => {
        const buttons: ActionButton[] = [];
        buttons.push({text: '取消', onClick: onCancelClick});
        buttons.push({text: '保存', onClick: onSaveClick, type: 'primary'});
        return buttons;
    };

    return (
        <PageContainer className="page-hold-buy">
            <ActionArea buttons={getButtons()} />
            <Form form={form}>
                <DataRow title="名称代码">
                    <Form.Item name={'code'}>
                        <Select showSearch optionFilterProp="children">
                            {infoBrief.map((item) => (
                                <Select.Option key={item.code} value={item.code}>
                                    {`${item.name}(${item.code})`}
                                </Select.Option>
                            ))}
                        </Select>
                    </Form.Item>
                </DataRow>
                <DataRow title="买入时间">
                    <Form.Item name={'buyTime'}>
                        <DatePicker />
                    </Form.Item>
                </DataRow>
                <DataRow title="买入价格">
                    <Form.Item name={'buyPrice'}>
                        <InputNumber className="data-desc price-input" stringMode step={'0.01'} min={0} />
                    </Form.Item>
                </DataRow>
                <DataRow title="买入数量">
                    <Form.Item name={'quantity'}>
                        <InputNumber className="data-desc price-input" stringMode step={'10'} min={0} />
                    </Form.Item>
                </DataRow>
                <DataRow title="买入说明">
                    <Form.Item name={'buyDescription'}>
                        <TextArea rows={4} className="data-desc" />
                    </Form.Item>
                </DataRow>
            </Form>
        </PageContainer>
    );
};

export default HoldBuy;
